import React, {useCallback, useEffect, useRef, useState} from 'react';
import {List} from 'immutable';
import {modes} from 'components/GraphingInput/GraphingInput.react';

import {GuessCorrectnessBanner} from '../shared/ContentBlocks';

import type {
  QuestionComponentProps
} from '../shared/QuestionComponent/QuestionComponent.react';
import {
  QuestionComponent
} from '../shared/QuestionComponent/QuestionComponent.react';

import useDraftGuess from '../../../AssignmentPracticeView/DraftGuesses/useDraftGuess';

import {SaveStatus} from '../QuestionType.types';

import GraphingQuestionInput from './GraphingQuestionInput/GraphingQuestionInput.react';

/**
 * The Graphing-specific store (GraphingQuestion.store) extends QuestionTypeStore and adds
 * getGuessEntities(). QuestionComponentProps types activeQuestionStore loosely (as the base
 * store class), so we narrow to the methods this component actually calls at runtime.
 */
interface GraphingQuestionStoreLike {
  getName(): string;
  getGuessEntities(): unknown[];
}

const GraphingQuestionComponent = (questionComponentProps: QuestionComponentProps) => {
  const {guess, activeQuestion, questionState, partialCreditEnabled} = questionComponentProps;
  const activeQuestionStore =
    questionComponentProps.activeQuestionStore as unknown as GraphingQuestionStoreLike | undefined;
  const {saveStatus} = useDraftGuess(
    activeQuestionStore?.getName?.() || '',
    questionState === 'PostGuess'
  );

  const lastQuestionStoreName = useRef(activeQuestionStore?.getName?.());
  const lastSaveStatus = useRef(saveStatus);

  const [draftGuessEntities, setDraftGuessEntities] = useState<unknown[] | undefined>([]);
  const [hasSetDraftGuessEntitites, setHasSetDraftGuessEntities] = useState(false);

  function getAdditionalGuessCorrectnessBannerCopy() {
    const submittedEntitiesCount = guess?.getIn(['content', 'entities'], List()).size;
    const expectedEntityCount = (activeQuestion.getValidResponse() || List()).size;

    if (submittedEntitiesCount > expectedEntityCount) {
      return 'Too many answers.';
    }

    if (submittedEntitiesCount > 0 && submittedEntitiesCount < expectedEntityCount) {
      return 'Incomplete answer.';
    }
  }

  useEffect(() => {
    if (
      !hasSetDraftGuessEntitites ||
      activeQuestionStore?.getName?.() !== lastQuestionStoreName.current ||
      (lastSaveStatus.current === SaveStatus.LOADING && saveStatus === SaveStatus.SAVED)
    ) {
      setDraftGuessEntities(activeQuestionStore?.getGuessEntities?.());
      setHasSetDraftGuessEntities(true);
    }

    lastQuestionStoreName.current = activeQuestionStore?.getName?.();
    lastSaveStatus.current = saveStatus;
  }, [activeQuestionStore, hasSetDraftGuessEntitites, saveStatus]);

  const customResultIndicator = (
    <>
      <GuessCorrectnessBanner
        additionalCopy={getAdditionalGuessCorrectnessBannerCopy()}
        guess={guess}
        partialCreditEnabled={partialCreditEnabled}
      />
      <GraphingQuestionInput
        draftGuessEntities={draftGuessEntities}
        guess={guess}
        isExplanation
        mode={modes.reviewing}
        question={activeQuestion}
        storeName={activeQuestionStore?.getName?.()}
      />
    </>
  );

  // memoizing the input area wrapper to prevent re-render loop when GQI updates guess entities
  const GraphingQuestionInputAreaWrapper = useCallback(
    (props) => (
      <GraphingQuestionInput
        {...props}
        draftGuessEntities={draftGuessEntities}
        guess={guess}
        mode={questionState === 'PreGuess' ? modes.guessing : modes.reviewing}
      />
    ),
    [draftGuessEntities, guess, questionState]
  );

  return (
    <QuestionComponent
      {...questionComponentProps}
      customHOA={<></>}
      customResultIndicator={customResultIndicator}
      ActiveQuestionInputArea={GraphingQuestionInputAreaWrapper}
    />
  );
};

type GraphingQuestionTypeProps = Omit<QuestionComponentProps, 'questionState'>;

const GraphingQuestion = {
  PreGuess: (props: GraphingQuestionTypeProps) => (
    <GraphingQuestionComponent {...props} questionState='PreGuess' />
  ),
  PostGuess: (props: GraphingQuestionTypeProps) => (
    <GraphingQuestionComponent {...props} questionState='PostGuess' />
  ),
  Dennis: (props: GraphingQuestionTypeProps) => (
    <GraphingQuestionComponent {...props} questionState='Dennis' />
  )
};

export default GraphingQuestion;
